import { ProjectStatus } from '../../../layouts/my-projects/project.const';

export interface StatusColorScheme {
  bg: string;
  flag: string;
  text: string;
  border: string;
  filterText: string;
}

export interface StatusOption {
  value: ProjectStatus | 'UNKNOWN' | 'NOT_AFFECTED';
  statusName: string;
  statusOrder: number;
  colorScheme: StatusColorScheme;
}

export const DEFAULT_STATUS_COLOR_SCHEME: StatusColorScheme = {
  bg: '#ffffff',
  flag: '#000000',
  text: '#000000',
  border: '#00000033',
  filterText: '#000000',
};

// Display-only fallback used when the project's status is null or not in PROJECT_STATUS_OPTIONS.
// Intentionally NOT part of PROJECT_STATUS_OPTIONS — the user can never pick it.
export const UNKNOWN_STATUS_OPTION: StatusOption = {
  value: 'UNKNOWN',
  statusName: 'Unknown',
  statusOrder: 99,
  colorScheme: {
    bg: '#d1d5db',
    flag: '#9ca3af',
    text: '#4b5563',
    border: '#d1d5db',
    filterText: '#4b5563',
  },
};

export const PROJECT_STATUS_OPTIONS: StatusOption[] = [
  {
    value: ProjectStatus.UpcomingExpiry,
    statusName: 'Upcoming',
    statusOrder: 1,
    colorScheme: {
      bg: '#fb923c',
      flag: '#fb923c',
      text: '#ffffff',
      border: '#fb923c',
      filterText: '#fb923c',
    },
  },
  {
    value: ProjectStatus.Completed,
    statusName: 'Completed',
    statusOrder: 2,
    colorScheme: {
      bg: '#22c55e',
      flag: '#22c55e',
      text: '#ffffff',
      border: '#22c55e',
      filterText: '#22c55e',
    },
  },
  {
    value: ProjectStatus.InProgress,
    statusName: 'In Progress',
    statusOrder: 3,
    colorScheme: {
      bg: '#3b82f6',
      flag: '#3b82f6',
      text: '#ffffff',
      border: '#3b82f6',
      filterText: '#3b82f6',
    },
  },
  {
    value: ProjectStatus.Delayed,
    statusName: 'Delayed',
    statusOrder: 4,
    colorScheme: {
      bg: '#ef4444',
      flag: '#ef4444',
      text: '#ffffff',
      border: '#ef4444',
      filterText: '#ef4444',
    },
  },
  {
    value: ProjectStatus.BLOCKED,
    statusName: 'Blocked',
    statusOrder: 5,
    colorScheme: {
      bg: '#b91c1c',
      flag: '#b91c1c',
      text: '#ffffff',
      border: '#b91c1c',
      filterText: '#b91c1c',
    },
  },
  {
    value: ProjectStatus.CANCELED,
    statusName: 'Canceled',
    statusOrder: 6,
    colorScheme: {
      bg: '#d1d5db',
      flag: '#9ca3af',
      text: '#4b5563',
      border: '#d1d5db',
      filterText: '#4b5563',
    },
  },
  {
    value: ProjectStatus.ToDo,
    statusName: 'To Do',
    statusOrder: 7,
    colorScheme: {
      bg: '#ffffff',
      flag: '#000000',
      text: '#000000',
      border: '#00000033',
      filterText: '#000000',
    },
  },
  {
    value: ProjectStatus.ARCHIVED,
    statusName: 'Archived',
    statusOrder: 8,
    colorScheme: {
      bg: '#d1d5db',
      flag: '#9ca3af',
      text: '#4b5563',
      border: '#d1d5db',
      filterText: '#4b5563',
    },
  },
];

// Project dropdown options — same as PROJECT_STATUS_OPTIONS but without ARCHIVED.
// Archive is a terminal state set via a separate action, not picked from the status dropdown.
// ARCHIVED stays in PROJECT_STATUS_OPTIONS so the badge still renders for archived projects.
export const PROJECT_DROPDOWN_OPTIONS: StatusOption[] = PROJECT_STATUS_OPTIONS.filter(
  (o) => o.value !== ProjectStatus.ARCHIVED
);

// Campaign status options — subset of the project statuses, no Archived.
export const CAMPAIGN_STATUS_OPTIONS: StatusOption[] = PROJECT_STATUS_OPTIONS.filter((o) =>
  (
    [
      ProjectStatus.UpcomingExpiry,
      ProjectStatus.Completed,
      ProjectStatus.InProgress,
      ProjectStatus.Delayed,
      ProjectStatus.BLOCKED,
      ProjectStatus.CANCELED,
      ProjectStatus.ToDo,
    ] as StatusOption['value'][]
  ).includes(o.value)
);

// Asset / variant statuses — same set as the campaign dropdown, no Archived.
export const ASSET_STATUS_OPTIONS: StatusOption[] = PROJECT_STATUS_OPTIONS.filter((o) =>
  (
    [
      ProjectStatus.UpcomingExpiry,
      ProjectStatus.Completed,
      ProjectStatus.InProgress,
      ProjectStatus.Delayed,
      ProjectStatus.BLOCKED,
      ProjectStatus.CANCELED,
      ProjectStatus.ToDo,
    ] as StatusOption['value'][]
  ).includes(o.value)
);

// Alternative status strings that should render the same badge as a canonical ProjectStatus.
// Asset-list entries use 'TO_DO' (see AssetListColor.TO_DO) while projects use 'TO_COME' —
// normalize so lookups against PROJECT_STATUS_OPTIONS still hit the "To Do" entry.
export const STATUS_VALUE_ALIASES: Record<string, ProjectStatus> = {
  TO_DO: ProjectStatus.ToDo,
};

// Display-only fallback for assets that aren't part of a project / haven't been affected yet.
// Not part of ASSET_STATUS_OPTIONS — never user-selectable.
// value: 'NOT_AFFECTED' so translateLabel resolves to the existing `task_NOT_AFFECTED` i18n key.
export const NOT_AFFECTED_STATUS_OPTION: StatusOption = {
  value: 'NOT_AFFECTED',
  statusName: 'Not Affected',
  statusOrder: 99,
  colorScheme: {
    bg: '#d1d5db',
    flag: '#9ca3af',
    text: '#4b5563',
    border: '#d1d5db',
    filterText: '#4b5563',
  },
};
